'use client';
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useModel } from '@/context/ModelContext';

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0].value;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className={`font-mono font-bold ${val >= 1.5 ? 'text-primary' : 'text-danger'}`}>
        DSCR: {val.toFixed(2)}x
      </p>
      <p className="text-muted-foreground mt-0.5">
        {val >= 1.5 ? '✓ Above covenant' : '⚠ Below 1.5x'}
      </p>
    </div>
  );
}

export default function DscrTrendChart() {
  const { model } = useModel();

  const data = model.years
    .slice(1)
    .filter((y) => y.dscr > 0)
    .map((y) => ({
      name: `Yr ${y.year}`,
      dscr: parseFloat(y.dscr.toFixed(2)),
    }));

  if (data.length === 0) {
    return (
      <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">
        No debt service years
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${v}x`}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine
          y={1.5}
          stroke="var(--danger)"
          strokeDasharray="4 4"
          strokeWidth={1.5}
          label={{
            value: '1.5x min',
            position: 'insideTopRight',
            fontSize: 10,
            fill: 'var(--danger)',
          }}
        />
        <Bar dataKey="dscr" radius={[4, 4, 0, 0]} maxBarSize={40}>
          {data.map((entry, idx) => (
            <Cell
              key={`dscr-bar-${idx}`}
              fill={entry.dscr >= 1.5 ? 'var(--primary)' : 'var(--danger)'}
              opacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
